import React, { useState, useTransition } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { toggleFavService } from "redux/services/general_service";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const ProductCard = ({ product, onAddToCart, onToggleWishlist }) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const handleQuickAdd = async () => {
    if (product.variants && product.variants.length > 0 && !selectedVariant) {
      setShowQuickAdd(true);
      return;
    }

    setIsLoading(true);
    try {
      await onAddToCart(product, selectedVariant);
      setShowQuickAdd(false);
      setSelectedVariant(null);
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWishlistToggle = () => {
    onToggleWishlist(product.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const calculateDiscountPercentage = () => {
    if (!product.price || !product.price_after_discount) return 0;
    return Math.round(
      ((product.price - product.price_after_discount) / product.price) * 100
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon
          key={i}
          name="Star"
          size={14}
          className="text-yellow-400 fill-current"
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon
          key="half"
          name="StarHalf"
          size={14}
          className="text-yellow-400 fill-current"
        />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon
          key={`empty-${i}`}
          name="Star"
          size={14}
          className="text-gray-300"
        />
      );
    }

    return stars;
  };

  const onToggleSelection = (e, id) => {
    e.stopPropagation();

    dispatch(toggleFavService({ product_id: id })).then((response) => {
      if (response.payload.status === 200) {
        window.location.reload();
      } else {
        toast.warn(t("sign_in_first"));
      }
    });
  };

  return (
    <>
      <div className="bg-global-6 rounded-[12px] p-[4px] sm:p-[6px] lg:p-[8px] shadow-[0px_4px_28px_#00000014]">
        {/* Product Image */}
        <div className="bg-global-6 rounded-[10px] p-[4px] sm:p-[6px] lg:p-[8px] mb-[4px] sm:mb-[6px] lg:mb-[8px] relative overflow-hidden">
          <Link to={`/product-detail?id=${product.id}`}>
            <Image
              src={product.images[0]?.file_path || product.category.image} // Use the first image or category image
              alt={product.name}
              className="w-full h-[132px] sm:h-[198px] lg:h-[264px] object-cover rounded-[10px] group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Badges */}
          <div className="absolute top-2 left-2 flex flex-col space-y-1">
            {product.has_discount && (
              <span className="bg-accent text-white text-xs font-medium px-2 py-1 rounded-full">
                -{calculateDiscountPercentage()}%
              </span>
            )}
          </div>

          <div className="absolute bottom-2 right-2 transition-smooth flex space-x-1">
            <button
              onClick={(e) => onToggleSelection(e, product.id)}
              className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-smooth shadow hover:shadow-md"
              aria-label={
                product.in_favorite
                  ? t("removeFromFavorites")
                  : t("addToFavorites")
              }
              onKeyDown={(e) => e.stopPropagation()}
            >
              {product.in_favorite ? (
                <Icon
                  name="Heart"
                  size={14}
                  className="text-red-500 fill-current"
                />
              ) : (
                <Icon name="Heart" size={14} className="text-gray-500" />
              )}
            </button>
            <button
              onClick={() => navigate(`/product-detail?id=${product.id}`)}
              className="w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-smooth shadow hover:shadow-md"
            >
              <Icon name="Eye" size={14} />
            </button>
          </div>

          {/* Quick Add Button - Desktop Only */}
          <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
            {/* <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={handleQuickAdd}
              loading={isLoading}
              iconName="Plus"
              iconPosition="left"
            >
              {t("addToCart")}
            </Button>*/}
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4">
          {/* Product Name */}
        <h4 className="font-semibold text-global-text2 mb-[2px] line-clamp-2 group-hover:text-primary transition-smooth">
  {product?.name}
</h4>

          <Link to={`/product-detail?id=${product.id}`}>
            {i18n.language === "en" ? (
              <h6 className="font-medium text-text-primary hover:text-primary transition-smooth line-clamp-2 mb-2">
                {product.category.translations?.en?.name}{" "}
                {/* Display English category name */}
              </h6>
            ) : (
              <h6 className="font-medium text-text-primary hover:text-primary transition-smooth line-clamp-2 mb-2">
                {product.category.translations?.ar?.name}{" "}
                {/* Display Arabic category name */}
              </h6>
            )}
          </Link>

          {/* Rating */}
          <div className="flex items-center space-x-1 mb-2">
            <div className="flex space-x-1">{renderStars(product?.rate)}</div>
            <span className="text-sm text-text-secondary">
              ({product?.reviews || 0})
            </span>
          </div>

          {/* Price */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-3">
            <span className="text-md font-semibold text-global-text2 font-mono">
              {product?.price_after_discount !== null
                ? formatPrice(product?.price_after_discount)
                : formatPrice(product?.price)}
            </span>
            {product?.price_after_discount !== null &&
              product?.price > product?.price_after_discount && (
                <span className="text-sm text-global-text6 line-through font-mono mt-1 sm:mt-0">
                  {formatPrice(product?.price)}
                </span>
              )}
          </div>

          {/* Mobile Add to Cart Button */}
          {/* <div className="md:hidden">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={handleQuickAdd}
              loading={isLoading}
              disabled={product.stock === 0}
              iconName="ShoppingCart"
              iconPosition="left"
            >
                 {t("addToCart")}
            </Button>
          </div>*/}
        </div>
      </div>

      {/* Quick Add Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-text-primary">
                  {t("selectOptions")}
                </h3>
                <button
                  onClick={() => setShowQuickAdd(false)}
                  className="text-text-secondary hover:text-text-primary transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-16 h-16 bg-muted rounded-md overflow-hidden flex-shrink-0">
                  <Image
                    src={product.images[0]?.file_path || product.category.image} // Use the first image or category image
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-text-primary line-clamp-2">
                    {product.category.translations?.en?.name}{" "}
                    {/* Display English category name */}
                  </h4>
                  <h4 className="font-medium text-text-primary line-clamp-2">
                    {product.category.translations?.ar?.name}{" "}
                    {/* Display Arabic category name */}
                  </h4>
                  <p className="text-lg font-semibold text-text-primary font-mono mt-1">
                    {product.price_after_discount
                      ? formatPrice(product.price_after_discount)
                      : formatPrice(product.price)}
                  </p>
                </div>
              </div>

              {/* Variants */}
              {product?.variants && product?.variants.length > 0 && (
                <div className="space-y-4">
                  {product?.variants?.map((variant) => (
                    <div key={variant.id}>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {variant.values[0].product_attribute_name}{" "}
                        {/* Display variant attribute name */}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {variant.values.map((option) => (
                          <button
                            key={option.id}
                            onClick={() =>
                              setSelectedVariant({
                                ...selectedVariant,
                                [variant.values[0].product_attribute_name]:
                                  option.value,
                              })
                            }
                            className={`px-3 py-2 text-sm border rounded-md transition-smooth ${
                              selectedVariant?.[
                                variant.values[0].product_attribute_name
                              ] === option.value
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-text-primary hover:border-primary"
                            }`}
                          >
                            {option.value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/*<div className="flex space-x-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowQuickAdd(false)}
                  fullWidth
                >
                  {t("cancel")}
                </Button>
                <Button
                  variant="default"
                  onClick={handleQuickAdd}
                  loading={isLoading}
                  fullWidth
                  iconName="ShoppingCart"
                  iconPosition="left"
                >
                 {t("addToCart")}
                </Button>
              </div>*/}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
